import { FILTERS, packFilter, type FilterId } from '../filters';
import type { Texture } from '../gl/texture';
import type { Engine } from './engine';
import type { DocumentResources } from './resources';

const AUX_BLUR = 8;
const AUX_SCRATCH = 9;

/** Filters that read a Gaussian-blurred copy of the source through `uAux`. */
function needsBlurAux(id: FilterId): boolean {
  return (
    id === 'gaussianBlur' ||
    id === 'highPass' ||
    id === 'unsharpMask' ||
    id === 'smartSharpen'
  );
}

function blurRadius(id: FilterId, values: Record<string, number>): number {
  if (id === 'gaussianBlur' || id === 'highPass' || id === 'unsharpMask' || id === 'smartSharpen') {
    return Math.max(0.1, values.radius ?? 1);
  }
  return 0;
}

function blurredCopy(
  engine: Engine,
  resources: DocumentResources,
  source: Texture,
  radius: number,
): Texture {
  const blurred = resources.rgba(AUX_BLUR);
  const scratch = resources.rgba(AUX_SCRATCH);
  const sigma = Math.max(0.6, radius / 2);
  const taps = Math.max(1, Math.min(64, Math.ceil(radius * 1.5)));
  engine.renderer.blurColorPass(scratch.framebuffer, source, 1 / source.width, 0, taps, sigma);
  engine.renderer.blurColorPass(
    blurred.framebuffer,
    scratch.texture,
    0,
    1 / source.height,
    taps,
    sigma,
  );
  return blurred.texture;
}

export interface FilterPass {
  type: number;
  params: Float32Array;
  aux: Texture | null;
  seed: number;
}

/** Everything the filter shader needs for one run. */
export function filterPass(
  engine: Engine,
  resources: DocumentResources,
  id: FilterId,
  values: Record<string, number>,
  source: Texture,
  seed = 0,
): FilterPass {
  const definition = FILTERS[id];
  const aux =
    needsBlurAux(id) ? blurredCopy(engine, resources, source, blurRadius(id, values)) : null;
  return {
    type: definition.type,
    params: packFilter(id, values),
    aux,
    seed,
  };
}
